import React, { useContext, useState, useEffect, useRef } from "react"
import { FirebaseContext } from "../../firebase"
import _ from "lodash"
import { useNavigate } from "react-router-dom"
import PageHeader from "../../components/PageHeader"
import { UserContext } from "../../context/userContext"
import { GET_USERS } from "./queries"
import GenericSqlTable from "../../components/DataTable/GenericSqlTable"
import { useUserAccountFilters } from "../../components/Filters/useUserAccountFilters"
import FilterHeader from "../../components/DataTable/FilterHeaderUserAccounts"
import { SplitButton } from "primereact/splitbutton"
import withDataFetching from "../../components/DataTable/withDataFetchingUserAccount"
import axios from "axios"
import cloudFunctionsEndpoint from "../../config/cloudFunctionsEndpoint"

const ID_TEMPLATE = "staff"

const ListUsers = ({
  data,
  page,
  setPage,
  limit,
  filters,
  setFilters,
  setSortField,
  setSortOrder,
  refetch,
}) => {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedUserPwdReset, setSelectedUserPwdReset] = useState({})
  const [selectedUserDisable, setSelectedUserDisable] = useState({})
  const endpoint = cloudFunctionsEndpoint()
  const firebase = useContext(FirebaseContext)

  const {
    clearAllFilters,
    onFilterData,
    onGlobalFilterChange,
    onGlobalEmailFilterChange,
    onGlobalUUIDFilterChange,
    // onBlurGlobalFilter,
    // onBlurGlobalEmailFilter,
    // onBlurGlobalUUIDFilter,
    onKeyPress,
    setGlobalFilterValue,
    setGlobalFilterEmailValue,
    setGlobalFilterUUIDValue,
    globalFilterValue,
    globalFilterEmailValue,
    globalFilterUUIDValue,
  } = useUserAccountFilters(refetch, setFilters)

  const previousFilters = useRef(filters)

  useEffect(() => {
    if (!_.isEqual(filters, previousFilters.current)) {
      setFilters(filters)
      previousFilters.current = filters
      refetch()
    }
  }, [filters, setFilters, refetch])

  useEffect(() => {
    setGlobalFilterValue(filters?.global?.value)
    setGlobalFilterEmailValue(filters?.staff_email_address?.value)
    setGlobalFilterUUIDValue(filters?.staff_id?.value)
  }, [])

  const handlePasswordReset = async (payload) => {
    setIsLoading(true) 

    try {
      if (payload?.values?.staff_email_address) {
        let actionCodeSettings = {
          // After password reset, the user will be give the ability to go back
          // to this page.
          url: window.location.origin,
          handleCodeInApp: false,
        }
        await firebase.sendPasswordResetEmail(_.trim(payload?.values?.staff_email_address), actionCodeSettings)
        await axios.post(`${endpoint}/sendPasswordResetEmail`, payload)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false) 
    }
  }

  const handleDisableAccount = async (payload) => {
    setIsLoading(true) 

    try {
      if (payload?.values?.staff_id) {
        await axios.post(`${endpoint}/disableAccount`, payload)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false) 
    }
  }

  useEffect(() => {
    if (selectedUserPwdReset) {
      handlePasswordReset(selectedUserPwdReset)
    }
  }, [selectedUserPwdReset])

  useEffect(() => {
    if (selectedUserDisable) {
      handleDisableAccount(selectedUserDisable)
    }
  }, [selectedUserDisable])

  const users = data?.allStaffs?.users
    ? data.allStaffs.users.map((staff) => ({
        ...staff,
        actions: (
          <SplitButton
            label="View"
            onClick={() => navigate(`/admin/users/${staff?.staff_id}`)}
            model={[
              {
                label: "Password Reset",
                command: () => {
                  setSelectedUserPwdReset({
                    idTemplate: "password_reset",
                    organization_alias: userCtx?.session?.user?.organization_alias,
                    organization_id: userCtx?.session?.user?.organization_id,
                    enterprise_id: userCtx?.session?.user?.enterprise_id,
                    enterprise_alias: userCtx?.session?.user?.enterprise_alias,
                    participant_alias: "not-set",
                    participant_membership_id: "not-set",
                    participant_id: "not-set",
                    staff_alias: userCtx?.session?.user?.staff_alias,
                    staff_id: userCtx?.session?.user?.staff_id,
                    values: {
                      staff_email_address: staff.staff_email_address,
                      staff_id: staff.staff_id,
                      signed_staff_name_first: staff.staff_name_first,
                      signed_staff_name_last: staff.staff_name_last,
                    },
                  })
                },
                disabled: isLoading,
              },
              // {
              //   label: "Disable Account",
              //   command: () => {
              //      setSelectedUserDisable({
              //       idTemplate: "disable_account",
              //       organization_alias: userCtx?.session?.user?.organization_alias,
              //       organization_id: userCtx?.session?.user?.organization_id,
              //       enterprise_id: userCtx?.session?.user?.enterprise_id,
              //       enterprise_alias: userCtx?.session?.user?.enterprise_alias,
              //       participant_alias: "not-set",
              //       participant_membership_id: "not-set",
              //       participant_id: "not-set",
              //       staff_alias: userCtx?.session?.user?.staff_alias,
              //       staff_id: userCtx?.session?.user?.staff_id,
              //        values: {
              //         staff_email_address: staff.staff_email_address,
              //         staff_id: staff.staff_id,
              //         signed_staff_name_first: staff.staff_name_first,
              //         signed_staff_name_last: staff.staff_name_last,
              //        },
              //     })
              //     console.log("Disable Account")
              //   },
              //   disabled: isLoading,
              // },
            ]}
          />
        ),
      }))
    : []

  const filterConfig = [
    {
      label: "",
      value: globalFilterValue,
      onChange: onGlobalFilterChange,
      // onBlur: onBlurGlobalFilter,
      onKeyDown: onKeyPress,
      placeholder: "Keyword Search",
      type: "text",
    },
    {
      label: "Email",
      value: globalFilterEmailValue,
      onChange: onGlobalEmailFilterChange,
      // onBlur: onBlurGlobalEmailFilter,
      onKeyDown: onKeyPress,
      placeholder: "Enter Email",
      type: "text",
    },
    {
      label: "UUID",
      value: globalFilterUUIDValue,
      onChange: onGlobalUUIDFilterChange,
      // onBlur: onBlurGlobalUUIDFilter,
      onkeydown: onKeyPress,
      placeholder: "Enter UUID",
      type: "text",
    },
  ]

  const pageActions = []

  const columns = [
    { field: "staff_name_first", header: "First Name", sortable: true },
    { field: "staff_name_last", header: "Last Name", sortable: true },
    { field: "staff_email_address", header: "Email", sortable: true },
    { field: "staff_id", header: "Staff ID (UUID)", sortable: true },
    { field: "actions", header: "Actions", sortable: false },
  ]

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const handleSortChange = (sortField, sortOrder) => {
    setSortField(sortField)
    setSortOrder(sortOrder)
  }

  return (
    <div className="main-content">
      <PageHeader title="Staff User Accounts" actions={pageActions} />
      <GenericSqlTable
        data={users}
        columns={columns}
        filters={filters}
        header={
          <FilterHeader
            filters={filterConfig}
            clearAllFilters={clearAllFilters}
            onFilterData={onFilterData}
          />
        }
        rows={limit}
        sortField="staff_name_first"
        sortOrder={1}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        totalCount={data?.allStaffs?.totalCount}
        currentPage={page}
      />
    </div>
  )
}

const WrappedListUsers = (props) => {
  const userCtx = useContext(UserContext)
  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [filters, setFilters] = useState({})
  const [sortField, setSortField] = useState("staff_name_first")
  const [sortOrder, setSortOrder] = useState(1)

  const variables = {
    staff_id: userCtx?.session.user?.staff_id,
    enterprise_id: userCtx?.session.user?.enterprise_id,
    organization_id: userCtx?.session.user?.organization_id,
    page,
    limit,
    filters: {
      global: filters?.global?.value,
      staff_email_address: filters?.staff_email_address?.value,
      staff_id: filters?.staff_id?.value,
    },
    sortField,
    sortOrder,
  }

  const EnhancedListUsers = withDataFetching(ListUsers, GET_USERS)

  return (
    <EnhancedListUsers
      {...props}
      variables={variables}
      page={page}
      setPage={setPage}
      limit={limit}
      filters={filters}
      setFilters={setFilters}
      setSortField={setSortField}
      setSortOrder={setSortOrder}
    />
  )
}

export default WrappedListUsers
